/**
 * Share links for skills.
 *
 * A skill has its own page — `/agents/skills/:skillId`, optionally
 * `/versions/v:version` — so a share link is just that page's URL. Nothing has
 * to be resolved against a list, a tab, or a filter for the recipient to land
 * on the right thing: the id is in the path, and the page fetches that one
 * skill. See `Goto.createSkillRoute` for the path itself; this module covers
 * the two things the route alone doesn't — making the path absolute, and
 * reading the version segment back out of it.
 */

import { Env } from "@/shared/environment/Env";
import { Goto } from "@/shared/navigation";

/**
 * Pre-route link param. The agent template's "Edit skill" link minted
 * `?skillId=…&name=…&tab=…`, and links carrying it are out in chat threads and
 * docs, so the Skills list page still reads it — to redirect to that skill's
 * page. The companion `name` and `tab` params existed only to aim a filtered
 * list at the right row on the right tab, a job the skill's own page doesn't
 * have, so they are read by nobody and go away with the redirect.
 */
export const LEGACY_SKILL_ID_PARAM = "skillId";

/**
 * The skill id an older query-param link points at, or null when the query
 * string carries none.
 */
export function parseLegacySkillLink(params: URLSearchParams): string | null {
  const raw = params.get(LEGACY_SKILL_ID_PARAM)?.trim();
  return raw === undefined || raw.length === 0 ? null : raw;
}

/**
 * Read a `:version` route segment as a version number.
 *
 * Accepts the `v3` spelling the app mints and a bare `3` for anyone who typed
 * the URL by hand. Returns null for anything else — a hand-mangled segment
 * must not resolve to some *other* version's body.
 */
export function parseSkillVersionParam(raw: string | undefined): number | null {
  if (raw === undefined) {
    return null;
  }
  const digits = raw.trim().replace(/^v/i, "");
  if (!/^\d+$/.test(digits)) {
    return null;
  }
  const parsed = Number(digits);
  return parsed >= 1 ? parsed : null;
}

/**
 * Marks the skill page as being in edit mode.
 *
 * A query param rather than a route segment because it names a *mode* over the
 * same resource, not a different one. Keeping it in the URL (instead of in
 * component state) is what lets the Skills table's per-version pen icon be an
 * ordinary link into the same page every other affordance lands on.
 */
export const SKILL_EDIT_PARAM = "edit";

/** The skill page, opened straight into edit mode. */
export function skillEditPath(
  skillId: string,
  version?: number | null,
): string {
  return `${Goto.createSkillRoute(skillId, version)}?${SKILL_EDIT_PARAM}=1`;
}

export interface SkillShareLinkParams {
  skillId: string;
  /** Omit (or pass null) to link the skill itself rather than one version. */
  version?: number | null;
}

/**
 * Fully-qualified URL of a skill's page, or of one of its versions.
 *
 * ``Env.appBasePath`` keeps branch-per-URL deploys working — building off
 * ``window.location.origin`` alone drops the React Router basename and lands
 * the recipient on a whitescreen.
 */
export function buildSkillShareLink({
  skillId,
  version,
}: SkillShareLinkParams): string {
  const url = new URL(
    `${Env.appBasePath}${Goto.createSkillRoute(skillId, version)}`,
    window.location.origin,
  );
  return url.toString();
}
